<script setup>
/**
 * The custom fields of one ticket type: what a ticket of this type is made of.
 *
 * The rail says how work moves; this says what the work is about. Each row
 * shows the label the team reads, the key the value is actually stored under,
 * and the shape the field collects — the key is on the row because it is the
 * one thing that cannot be changed later, and an admin should be able to see it
 * without opening a dialog.
 *
 * Reordering emits the whole list of ids: the server takes it as a permutation
 * and refuses anything else, so a half-applied drag is not a state this editor
 * can produce. Removal is archival — the values already collected under a key
 * stay on their tickets, the field just stops being asked for.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';

import Button from 'dashboard/components-next/button/Button.vue';
import { fieldTypeMeta } from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  // `{ id, key, label, fieldType, options, required, position }`, in list order.
  fieldDefinitions: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['add', 'edit', 'remove', 'reorder']);

const { t } = useI18n();

const orderedFields = computed({
  get: () => props.fieldDefinitions,
  set: value =>
    emit(
      'reorder',
      value.map(definition => definition.id)
    ),
});

const typeLabel = definition => t(fieldTypeMeta(definition.fieldType).labelKey);

const typeIcon = definition => fieldTypeMeta(definition.fieldType).icon;
</script>

<template>
  <section class="flex flex-col gap-3" data-testid="fields-editor">
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-0.5">
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.SETTINGS.FIELDS.TITLE') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('SUPPORT_CASES.SETTINGS.FIELDS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        color="slate"
        icon="i-lucide-plus"
        :label="t('SUPPORT_CASES.SETTINGS.FIELDS.ADD')"
        :disabled="disabled"
        data-testid="field-add"
        @click="emit('add')"
      />
    </div>

    <p
      v-if="!fieldDefinitions.length"
      class="p-3 mb-0 text-xs rounded-lg text-n-slate-11 bg-n-alpha-1"
      data-testid="fields-empty"
    >
      {{ t('SUPPORT_CASES.SETTINGS.FIELDS.EMPTY') }}
    </p>

    <Draggable
      v-model="orderedFields"
      item-key="id"
      handle="[data-drag-handle]"
      :disabled="disabled"
      class="flex flex-col gap-2"
      ghost-class="opacity-40"
    >
      <template #item="{ element: definition }">
        <div
          class="flex items-center gap-3 p-3 border rounded-lg border-n-weak bg-n-solid-1"
          :data-testid="`field-row-${definition.id}`"
        >
          <span
            data-drag-handle
            class="text-n-slate-10 i-lucide-grip-vertical size-4 shrink-0"
            :class="disabled ? '' : 'cursor-grab'"
          />

          <div class="flex flex-col min-w-0 gap-1">
            <span class="text-sm truncate text-n-slate-12">
              {{ definition.label }}
              <span
                v-if="definition.required"
                class="text-n-ruby-9"
                :title="t('SUPPORT_CASES.SETTINGS.FIELDS.REQUIRED_LABEL')"
                :data-testid="`field-required-${definition.id}`"
              >
                *
              </span>
            </span>
            <span
              class="font-mono text-xs truncate text-n-slate-11"
              :data-testid="`field-key-${definition.id}`"
            >
              {{ definition.key }}
            </span>
          </div>

          <div class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto">
            <span
              class="flex items-center gap-1 text-xs text-n-slate-11"
              :data-testid="`field-type-${definition.id}`"
            >
              <span :class="typeIcon(definition)" class="size-3.5 shrink-0" />
              {{ typeLabel(definition) }}
            </span>
            <span
              v-if="definition.options && definition.options.length"
              class="text-xs tabular-nums text-n-slate-11"
              :data-testid="`field-options-${definition.id}`"
            >
              {{
                t('SUPPORT_CASES.SETTINGS.FIELDS.OPTION_COUNT', {
                  count: definition.options.length,
                })
              }}
            </span>
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-pencil"
              :disabled="disabled"
              :aria-label="t('SUPPORT_CASES.SETTINGS.FIELDS.EDIT')"
              :data-testid="`field-edit-${definition.id}`"
              @click="emit('edit', definition)"
            />
            <Button
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-trash-2"
              :disabled="disabled"
              :aria-label="t('SUPPORT_CASES.SETTINGS.FIELDS.REMOVE')"
              :data-testid="`field-remove-${definition.id}`"
              @click="emit('remove', definition)"
            />
          </div>
        </div>
      </template>
    </Draggable>
  </section>
</template>
